import type {
  AnalysisVerificationResult,
  SpectralAnalysisListItem,
  SpectralAnalysisSnapshot,
  SpectralAnalysisStore,
} from '../types';
import { API_BASE_URL } from '@types';

const normalizeSnapshot = (
  snapshot: SpectralAnalysisSnapshot
): SpectralAnalysisSnapshot => ({
  ...snapshot,
  verification: snapshot.verification ?? {
    status: 'not-run',
    notes: ['Verifica non ancora eseguita'],
  },
  groupSummary: {
    ...snapshot.groupSummary,
    aggregatedHrTimeline: snapshot.groupSummary?.aggregatedHrTimeline ?? [],
    aggregatedPsd: {
      baseline: snapshot.groupSummary?.aggregatedPsd?.baseline ?? {
        frequencies: [],
        psd_lf: [],
        psd_hf: [],
      },
      exposure: snapshot.groupSummary?.aggregatedPsd?.exposure ?? {
        frequencies: [],
        psd_lf: [],
        psd_hf: [],
      },
    },
    statisticalValidation: snapshot.groupSummary?.statisticalValidation ?? [],
  },
});

const buildUrl = (path: string) =>
  `${API_BASE_URL}/services/v1/research/spectral-analyses${path}`;

export const spectralAnalysisStore: SpectralAnalysisStore = {
  async createAnalysis(snapshot) {
    const response = await fetch(buildUrl(''), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        analysisType: 'spectral_normalization',
        status: 'completed',
        snapshot,
      }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Salvataggio analisi fallito: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as {
      snapshot?: SpectralAnalysisSnapshot;
    };
    if (!parsed.snapshot) {
      throw new Error('Risposta backend incompleta: snapshot mancante.');
    }

    return normalizeSnapshot(parsed.snapshot);
  },

  async updateAnalysis(id, snapshot) {
    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        status: 'completed',
        snapshot,
      }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Aggiornamento analisi fallito: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as {
      snapshot?: SpectralAnalysisSnapshot;
    };
    if (!parsed.snapshot) {
      throw new Error('Risposta backend incompleta: snapshot mancante.');
    }

    return normalizeSnapshot(parsed.snapshot);
  },

  async getAnalysis(id) {
    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'GET',
      credentials: 'include',
    });

    if (response.status === 404) return null;

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Recupero analisi fallito: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as {
      snapshot?: SpectralAnalysisSnapshot | null;
    };

    return parsed.snapshot ? normalizeSnapshot(parsed.snapshot) : null;
  },

  async listAnalyses() {
    const response = await fetch(buildUrl(''), {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Lista analisi fallita: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as SpectralAnalysisListItem[];
    return Array.isArray(parsed)
      ? parsed.sort((left, right) =>
          right.createdAt.localeCompare(left.createdAt)
        )
      : [];
  },

  async updateVerification(id, verification: AnalysisVerificationResult) {
    void verification;
    return spectralAnalysisStore.getAnalysis(id);
  },
};
